import React, { useState } from "react";
import { Box, Typography, Tooltip, Chip, Stack, Collapse } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";

// ---------------------------------------------------------------------------
// Data helpers
// ---------------------------------------------------------------------------

export function buildRunStats(task, manifest) {
  const manifestCounts = {};
  (manifest || []).forEach((m) => {
    const s = m.status || "unknown";
    manifestCounts[s] = (manifestCounts[s] || 0) + 1;
  });

  const hasManifest = Boolean(manifest?.length);

  const discovered = task.urls_discovered ||
    (hasManifest ? manifest.length : (task.files_skipped_url || 0) + (task.files_downloaded || 0) + (task.files_download_failed || 0));
  const seen = task.urls_seen || task.files_skipped_url || 0;
  const newUrls = Math.max(0, discovered - seen);
  const downloaded = task.files_downloaded || 0;
  const downloadFailed = task.files_download_failed || 0;
  const notPdf = task.files_not_pdf ||
    (hasManifest ? (manifestCounts.skipped_not_pdf || 0) : 0);
  const pdfCount = Math.max(0, downloaded - notPdf);
  const hashDup = task.files_skipped_hash || 0;
  const uploadFailed = task.files_upload_failed || 0;
  const newDocs = task.files_new || 0;

  let durationSec = null;
  if (task.started_at && task.completed_at) {
    durationSec = Math.max(
      0,
      Math.round(
        (new Date(task.completed_at).getTime() - new Date(task.started_at).getTime()) / 1000,
      ),
    );
  }

  const daysBack = task.resolved_config?.scraping?.days_back;

  return {
    discovered,
    seen,
    newUrls,
    downloaded,
    downloadFailed,
    notPdf,
    pdfCount,
    hashDup,
    uploadFailed,
    newDocs,
    hasManifest,
    durationSec,
    daysBack,
    manifestCounts,
  };
}

export function formatRunDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

const PipelineSummary = ({ stats }) => {
  const lines = [
    `Discovered (${stats.discovered})`,
    `  Seen URL (prior run) → skip${stats.seen ? ` (${stats.seen})` : ""}`,
    `  New URL${stats.newUrls ? ` (${stats.newUrls})` : ""}`,
    `    Download failed${stats.downloadFailed ? ` (${stats.downloadFailed})` : ""} — retryable`,
    `    Downloaded${stats.downloaded ? ` (${stats.downloaded})` : ""}`,
    `      Not PDF${stats.notPdf ? ` (${stats.notPdf})` : ""} — permanent skip`,
    `      PDF${stats.pdfCount ? ` (${stats.pdfCount})` : ""}`,
    `        Hash duplicate${stats.hashDup ? ` (${stats.hashDup})` : ""} — permanent skip`,
    `        New hash → upload`,
    `          Upload failed${stats.uploadFailed ? ` (${stats.uploadFailed})` : ""} — retryable`,
    `          Uploaded${stats.newDocs ? ` (${stats.newDocs})` : ""} → document row`,
  ];

  return (
    <Box sx={{ mb: 1.5, p: 1, bgcolor: "background.paper", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
        Pipeline
      </Typography>
      <Typography variant="caption" component="pre" sx={{ fontFamily: "monospace", fontSize: 11, m: 0, whiteSpace: "pre-wrap" }}>
        {lines.join("\n")}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Expanded view — pipeline tree + manifest summary chips + failed items + config toggle
// ---------------------------------------------------------------------------

const ScrapingRunStats = ({ task, manifest, resolvedConfig }) => {
  const stats = buildRunStats(task, manifest);
  const config = resolvedConfig || task.resolved_config;
  const [showConfig, setShowConfig] = useState(false);
  const manifestItems = manifest || [];
  const failed = manifestItems.filter(
    (m) => m.status === "failed_download" || m.status === "failed_upload" || m.status === "failed_db_insert",
  );

  return (
    <Box>
      <PipelineSummary stats={stats} />

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap" }}>
        <Typography variant="subtitle2">File Manifest</Typography>
        {stats.daysBack != null && (
          <Chip label={`${stats.daysBack}d window`} size="small" variant="outlined" />
        )}
        {stats.durationSec != null && (
          <Chip label={formatRunDuration(stats.durationSec)} size="small" variant="outlined" />
        )}
        {stats.newDocs > 0 ? (
          <Chip label={`${stats.newDocs} new documents`} size="small" color="success" />
        ) : stats.seen === stats.discovered && stats.discovered > 0 ? (
          <Chip label="All URLs known — nothing to fetch" size="small" color="default" />
        ) : null}
      </Stack>

      {manifestItems.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            {manifestItems.length} total URLs in manifest
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            {Object.entries(stats.manifestCounts).map(([status, count]) => (
              <Chip
                key={status}
                label={`${status.replace(/_/g, " ")}: ${count}`}
                size="small"
                variant="outlined"
                color={
                  status === "uploaded" ? "success"
                    : status.startsWith("failed") ? "error"
                    : status === "skipped_not_pdf" ? "warning"
                    : status === "skipped_url" ? "default"
                    : "primary"
                }
              />
            ))}
          </Box>
        </>
      )}

      {failed.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="error.main" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Failed items ({failed.length})
          </Typography>
          {failed.map((f, i) => (
            <Typography key={i} variant="caption" sx={{ display: "block", fontFamily: "monospace", fontSize: 11, ml: 1 }}>
              {f.original_name || f.url?.slice(-40)} — {f.error || f.status}
            </Typography>
          ))}
        </Box>
      )}

      {config && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, cursor: "pointer", userSelect: "none", "&:hover": { color: "text.primary" } }}
            onClick={(e) => { e.stopPropagation(); setShowConfig((v) => !v); }}
          >
            {showConfig ? "Hide config" : "Show config"}
          </Typography>
          <Collapse in={showConfig} timeout="auto" unmountOnExit>
            <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: 11, whiteSpace: "pre-wrap", display: "block", mt: 0.5 }}>
              {JSON.stringify(config, null, 2)}
            </Typography>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default ScrapingRunStats;
